import React, { useMemo } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Button,
} from "@mui/material";
import { styled } from "@mui/system";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

// Custom styled components
const CustomTableCell = styled(TableCell)(({ columnWidth }) => ({
  backgroundColor: "#424752",
  fontSize: "13px",
  textAlign: "center",
  color: "white",
  border: "1px solid #e0e0e0",
  padding: "8px 12px",
  minWidth: columnWidth || 100,
  maxWidth: columnWidth || 100,
  whiteSpace: "normal",
  wordWrap: "break-word",
  overflow: "hidden",
  textOverflow: "ellipsis",
}));

const StickyTableCell = styled(CustomTableCell)(({ left, isLastSticky }) => ({
  position: "sticky",
  left: left || 0,
  zIndex: 3,
  boxShadow: isLastSticky ? "2px 0 4px rgba(0,0,0,0.2)" : "none",
}));

const CustomTableRow = styled(TableRow)(({ index }) => ({
  backgroundColor: index % 2 === 0 ? "#FFFFFF" : "#F7F7F7",
}));

const BodyTableCell = styled(TableCell)(({ columnWidth }) => ({
  border: "0px solid #e0e0e0",
  padding: "8px 12px",
  textAlign: "center",
  fontSize: "12px",
  minWidth: columnWidth || 100,
  maxWidth: columnWidth || 100,
  whiteSpace: "normal",
  wordWrap: "break-word",
  overflow: "hidden",
  textOverflow: "ellipsis",
}));

const StickyBodyTableCell = styled(BodyTableCell)(({ left, isLastSticky, rowIndex }) => ({
  position: "sticky",
  left: left || 0,
  zIndex: 1,
  backgroundColor: rowIndex % 2 === 0 ? "#FFFFFF" : "#F7F7F7",
  boxShadow: isLastSticky ? "2px 0 4px rgba(0,0,0,0.2)" : "none",
}));

// Helper function to format date
const formatDate = (date) => {
  if (!date) return "";
  try {
    const d = new Date(date);
    return d.toISOString().split("T")[0];
  } catch {
    return "";
  }
};

// Helper function to estimate text width with a cap
const estimateTextWidth = (text) => {
  if (!text) return 100;
  const charWidth = 8;
  const padding = 24;
  const estimatedWidth = text.length * charWidth + padding;
  return Math.min(estimatedWidth, 8 * window.innerWidth / 100);
};

// Helper function to generate all dates in range
const getDateRange = (startDate, endDate) => {
  const dates = [];
  if (!startDate || !endDate) return dates;
  const start = new Date(startDate);
  const end = new Date(endDate);
  if (isNaN(start.getTime()) || isNaN(end.getTime())) return [];
  for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
    dates.push(formatDate(new Date(d)));
  }
  return dates;
};

const DailyVolumeTable = ({ data, startDate, endDate, shifts, subForm }) => {
  // Debug: Log received props
  console.log("DailyVolumeTable props:", { data, startDate, endDate, shifts, subForm });

  // Define static table headers
  const staticHeaders = [
    { id: "line", label: "Line" },
    { id: "wattage", label: "Wattage" },
  ];

  // Get all dates in the range
  const dates = getDateRange(startDate, endDate);

  // Use provided shifts, fallback to data if shifts prop is empty
  const effectiveShifts = shifts.length > 0 ? shifts : [
    ...new Set(data.map((item) => item?.inspectionShift).filter(Boolean))
  ].sort();
  console.log("Effective Shifts:", effectiveShifts);

  // Create dynamic headers for all dates and shifts
  const dynamicHeaders = dates.flatMap((date) =>
    effectiveShifts.map((shift) => ({
      id: `${date}_${shift}`,
      label: `${date} - ${shift}`,
      date,
      shift,
      subHeaders: [
        { id: `layupStartSerialNo_${date}_${shift}`, label: "Layup Start Serial No" },
        { id: `layupEndSerialNo_${date}_${shift}`, label: "Layup End Serial No" },
        { id: `layupTotalQty_${date}_${shift}`, label: "Layup Total Qty" },
        { id: `quantity_${date}_${shift}`, label: "Packed Quantity" },
        { id: `mwp_${date}_${shift}`, label: "MWp" },
        { id: `cumulativeMwp_${date}_${shift}`, label: "Cumulative MWp" },
      ],
    }))
  );
  console.log("Dynamic Headers:", dynamicHeaders);

  // Calculate column widths based on content
  const columnWidths = useMemo(() => {
    const widths = {
      line: estimateTextWidth("Line"),
      wattage: estimateTextWidth("Wattage"),
      layupStartSerialNo: estimateTextWidth("Layup Start Serial No"),
      layupEndSerialNo: estimateTextWidth("Layup End Serial No"),
      layupTotalQty: estimateTextWidth("Layup Total Qty"),
      quantity: estimateTextWidth("Packed Quantity"),
      mwp: estimateTextWidth("MWp"),
      cumulativeMwp: estimateTextWidth("Cumulative MWp"),
    };

    data.forEach((item) => {
      (item.form_data?.lines || []).forEach((line) => {
        widths.line = Math.min(
          Math.max(widths.line, estimateTextWidth(line.line)),
          8 * window.innerWidth / 100
        );
        Object.keys(line.packedQuantity || {}).forEach((wattage) => {
          widths.wattage = Math.min(
            Math.max(widths.wattage, estimateTextWidth(wattage)),
            8 * window.innerWidth / 100
          );
          const pq = line.packedQuantity[wattage];
          widths.quantity = Math.min(
            Math.max(widths.quantity, estimateTextWidth(pq.quantity)),
            8 * window.innerWidth / 100
          );
          widths.mwp = Math.min(
            Math.max(widths.mwp, estimateTextWidth(pq.MWp)),
            8 * window.innerWidth / 100
          );
          widths.cumulativeMwp = Math.min(
            Math.max(widths.cumulativeMwp, estimateTextWidth(String(pq.cumulativeMWp))),
            8 * window.innerWidth / 100
          );
          widths.layupStartSerialNo = Math.min(
            Math.max(widths.layupStartSerialNo, estimateTextWidth(line.layup?.startSerialNo)),
            8 * window.innerWidth / 100
          );
          widths.layupEndSerialNo = Math.min(
            Math.max(widths.layupEndSerialNo, estimateTextWidth(line.layup?.endSerialNo)),
            8 * window.innerWidth / 100
          );
          widths.layupTotalQty = Math.min(
            Math.max(widths.layupTotalQty, estimateTextWidth(line.layup?.totalQty)),
            8 * window.innerWidth / 100
          );
        });
      });
    });

    dynamicHeaders.forEach((header) => {
      header.subHeaders.forEach((subHeader) => {
        widths[subHeader.id] = widths[subHeader.id.split("_")[0]];
      });
    });

    return widths;
  }, [data, dynamicHeaders]);

  // Calculate cumulative left positions for sticky columns
  const getLeftPosition = (index) => {
    const staticKeys = ["line", "wattage"];
    return staticKeys
      .slice(0, index)
      .reduce((sum, key) => sum + columnWidths[key], 0);
  };

  // Prepare table rows by grouping by line and wattage
  const tableRows = useMemo(() => {
    // Create a map to group data by line and wattage
    const rowMap = new Map();

    data.forEach((item) => {
      const dateKey = formatDate(item.inspectionDate);
      const shift = item.inspectionShift;
      (item.form_data?.lines || []).forEach((line) => {
        Object.entries(line.packedQuantity || {}).forEach(([wattage, pq]) => {
          const key = `${line.line}_${wattage}`;
          if (!rowMap.has(key)) {
            rowMap.set(key, {
              line: line.line,
              wattage,
              data: {},
            });
          }
          const row = rowMap.get(key);
          row.data[`${dateKey}_${shift}`] = {
            layupStartSerialNo: line.layup?.startSerialNo || "N/A",
            layupEndSerialNo: line.layup?.endSerialNo || "N/A",
            layupTotalQty: line.layup?.totalQty || "N/A",
            quantity: pq.quantity || "N/A",
            mwp: pq.MWp || "N/A",
            cumulativeMwp: pq.cumulativeMWp || "N/A",
          };
        });
      });
    });

    // Convert map to array and sort by line and wattage
    return Array.from(rowMap.values()).sort((a, b) => {
      if (a.line === b.line) {
        return a.wattage.localeCompare(b.wattage);
      }
      return a.line.localeCompare(b.line);
    });
  }, [data]);

  // Filter dynamic headers based on date range and shifts
  const filteredDynamicHeaders = dynamicHeaders.filter((header) => {
    const dateInRange =
      (!startDate || header.date >= formatDate(startDate)) &&
      (!endDate || header.date <= formatDate(endDate));
    const shiftMatch = effectiveShifts.includes(header.shift);
    return dateInRange && shiftMatch;
  });

  const exportToExcel = () => {
    const wb = XLSX.utils.book_new();

    // Row 1: Dates (merged across 6 sub-columns)
    const row1 = ["Line", "Wattage"];
    const merges = [];
    let colIdx = 2;

    filteredDynamicHeaders.forEach((header) => {
      const startCol = colIdx;
      // 6 sub-columns per date-shift
      for (let i = 0; i < 6; i++) row1.push(header.label);
      if (6 > 1) {
        merges.push({ s: { r: 0, c: startCol }, e: { r: 0, c: startCol + 5 } });
      }
      colIdx += 6;
    });

    // Row 2: Sub-column headers (repeated under each date-shift)
    const row2 = ["Line", "Wattage"];
    filteredDynamicHeaders.forEach(() => {
      row2.push(
        "Layup Start Serial No",
        "Layup End Serial No",
        "Layup Total Qty",
        "Packed Quantity",
        "MWp",
        "Cumulative MWp"
      );
    });

    // Data rows
    const dataRows = tableRows.map((row) => {
      const r = [row.line, row.wattage];
      filteredDynamicHeaders.forEach((header) => {
        const key = header.id;
        const cellData = row.data[key] || {};
        r.push(
          cellData.layupStartSerialNo || "",
          cellData.layupEndSerialNo || "",
          cellData.layupTotalQty || "",
          cellData.quantity || "",
          cellData.mwp || "",
          cellData.cumulativeMwp || ""
        );
      });
      return r;
    });

    const sheetData = [row1, row2, ...dataRows];
    const ws = XLSX.utils.aoa_to_sheet(sheetData);

    // Apply merges for date row
    if (merges.length > 0) ws["!merges"] = merges;

    // Column widths
    ws["!cols"] = [
      { wch: 12 }, // Line
      { wch: 12 }, // Wattage
      ...Array(filteredDynamicHeaders.length * 6).fill({ wch: 18 }),
    ];

    XLSX.utils.book_append_sheet(wb, ws, "Daily Volume");

    const fileName = `Daily_Volume_${formatDate(startDate || new Date())}_to_${formatDate(endDate || new Date())}.xlsx`;
    const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });

    saveAs(new Blob([excelBuffer], { type: "application/octet-stream" }), fileName);
  };

  return (
    <div style={{ margin: "20px", overflowX: "auto", maxWidth: "100vw" }}>
      <Typography variant="h6" gutterBottom>
        Daily Volume of Qualified Modules
      </Typography>

      <Button
        variant="contained"
        color="success"
        onClick={exportToExcel}
        sx={{ mb: 2 }}
        disabled={tableRows.length === 0 || filteredDynamicHeaders.length === 0}
      >
        Download as Excel
      </Button>
      
      <TableContainer component={Paper} sx={{ maxWidth: "100%", overflowX: "auto" }}>
        <Table sx={{ tableLayout: "fixed", width: "max-content" }}>
          <TableHead>
            <TableRow>
              {staticHeaders.map((header, index) => (
                <StickyTableCell
                  key={header.id}
                  rowSpan={2}
                  columnWidth={columnWidths[header.id]}
                  left={getLeftPosition(index)}
                  isLastSticky={index === staticHeaders.length - 1}
                >
                  {header.label}
                </StickyTableCell>
              ))}
              {filteredDynamicHeaders.map((header) => (
                <CustomTableCell
                  key={header.id}
                  colSpan={header.subHeaders.length}
                  columnWidth={header.subHeaders.reduce((sum, sh) => sum + columnWidths[sh.id], 0)}
                >
                  {header.label}
                </CustomTableCell>
              ))}
            </TableRow>
            <TableRow>
              {filteredDynamicHeaders.map((header) =>
                header.subHeaders.map((subHeader) => (
                  <CustomTableCell
                    key={subHeader.id}
                    columnWidth={columnWidths[subHeader.id]}
                  >
                    {subHeader.label}
                  </CustomTableCell>
                ))
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {tableRows.length === 0 ? (
              <CustomTableRow index={0}>
                <StickyBodyTableCell
                  colSpan={staticHeaders.length + filteredDynamicHeaders.reduce((sum, h) => sum + h.subHeaders.length, 0)}
                  align="center"
                  rowIndex={0}
                  columnWidth={columnWidths.line}
                  left={getLeftPosition(0)}
                  isLastSticky={false}
                >
                  No data available
                </StickyBodyTableCell>
              </CustomTableRow>
            ) : (
              tableRows.map((row, index) => (
                <CustomTableRow key={`${row.line}_${row.wattage}_${index}`} index={index}>
                  <StickyBodyTableCell
                    rowIndex={index}
                    columnWidth={columnWidths.line}
                    left={getLeftPosition(0)}
                    isLastSticky={false}
                  >
                    {row.line}
                  </StickyBodyTableCell>
                  <StickyBodyTableCell
                    rowIndex={index}
                    columnWidth={columnWidths.wattage}
                    left={getLeftPosition(1)}
                    isLastSticky={true}
                  >
                    {row.wattage}
                  </StickyBodyTableCell>
                  {filteredDynamicHeaders.map((header) => {
                    const data = row.data[header.id] || {};
                    return header.subHeaders.map((subHeader) => (
                      <BodyTableCell
                        key={`${subHeader.id}_${index}`}
                        columnWidth={columnWidths[subHeader.id]}
                      >
                        {data[subHeader.id.split("_")[0]] || "N/A"}
                      </BodyTableCell>
                    ));
                  })}
                </CustomTableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </div>
  );
};

export default DailyVolumeTable;